import { useState } from "react";
import api from "../lib/api";
import { ArrowRight, Smartphone, Building2, DollarSign, TrendingUp } from "lucide-react";
import { toast } from "sonner";

export default function SalesTeam() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "United States", city: "", linkedin: "",
    annual_sales_target: 250000, industries_sold: "", delaware_c_corp_familiar: false, work_mobile_only: false, pitch: "",
  });
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/sales-team/signup", form);
      setResult(data);
      if (data.qualified) toast.success("Power Sales application received.");
      else toast.success("Application received.");
    } catch { toast.error("Submission failed."); }
  };

  return (
    <>
      <section className="bg-[#064E3B] text-white" data-testid="sales-team-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="federal-stamp" style={{ borderColor: "#FACC15", color: "#FACC15", background: "rgba(250,204,21,0.08)" }}>
            ★ POWER SALES · 22% COMMISSION · OWNERSHIP POTENTIAL
          </div>
          <h1 className="display-black text-5xl lg:text-6xl uppercase mt-6 tracking-tighter max-w-4xl leading-[0.95]">
            Join our<br />Power Sales Team.
          </h1>
          <p className="text-lg text-white/85 mt-6 max-w-2xl">
            Close a minimum of <span className="text-[#FACC15] font-semibold">$250,000 in annual sales</span> and earn a <span className="text-[#FACC15] font-semibold">22% commission</span> with documented <span className="text-[#FACC15] font-semibold">ownership potential</span>. We seek Delaware-corporate-minded operators ready for a C-Corp structure. Work from your smartphone — worldwide.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-300 border border-gray-300 mb-16" data-testid="sales-perks">
            {[
              { icon: DollarSign, t: "22%", l: "Commission Rate" },
              { icon: TrendingUp, t: "$250K+", l: "Minimum Annual" },
              { icon: Building2, t: "C-Corp", l: "Ownership Path" },
              { icon: Smartphone, t: "Mobile", l: "Work Anywhere" },
            ].map(({ icon: Icon, t, l }) => (
              <div key={l} className="bento-card p-8 text-center">
                <Icon className="w-7 h-7 mx-auto text-[#D97706]" strokeWidth={2} />
                <div className="display-black text-3xl text-[#064E3B] mt-4">{t}</div>
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-gray-600 mt-2">{l}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            <aside className="lg:col-span-2 space-y-6">
              <div className="border border-gray-300 p-7">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// What you get</div>
                <h3 className="display-black text-xl uppercase text-[#064E3B] mt-3">Power Sales Tier</h3>
                <ul className="mt-4 space-y-3 text-sm text-gray-800">
                  <li className="flex gap-3"><span className="text-[#D97706] font-bold">▸</span> 22% commission on every closed engagement</li>
                  <li className="flex gap-3"><span className="text-[#D97706] font-bold">▸</span> Documented ownership pathway · Delaware C-Corp</li>
                  <li className="flex gap-3"><span className="text-[#D97706] font-bold">▸</span> Mobile-first dispatch — sell from your smartphone</li>
                  <li className="flex gap-3"><span className="text-[#D97706] font-bold">▸</span> Worldwide territory rights once $250K bar is cleared</li>
                  <li className="flex gap-3"><span className="text-[#D97706] font-bold">▸</span> Direct access to large-job channel + consulting overflow</li>
                </ul>
              </div>
              <div className="border border-[#D97706] bg-red-50 p-7">
                <div className="mono text-[10px] tracking-[0.22em] uppercase text-[#D97706]">// Who we want</div>
                <ul className="mt-4 space-y-2 text-sm text-gray-900">
                  <li>• Delaware-corporate-minded operators</li>
                  <li>• Comfortable with C-Corp structure & equity vesting</li>
                  <li>• Track record of high-ticket B2B sales</li>
                  <li>• Mobile-first, work-from-anywhere mentality</li>
                </ul>
              </div>
            </aside>

            <div className="lg:col-span-3">
              {result ? (
                <div className={`border p-10 ${result.qualified ? "border-[#064E3B] bg-gray-50" : "border-gray-300 bg-white"}`} data-testid="sales-result">
                  <div className="display-black text-2xl uppercase text-[#064E3B]">
                    {result.qualified ? "★ Power Sales — Application Received" : "Application Received"}
                  </div>
                  <p className="text-gray-800 mt-3">{result.message}</p>
                  <button onClick={() => setResult(null)} className="btn-ghost mt-6">Submit another</button>
                </div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5 border border-gray-300 p-8" data-testid="sales-form">
                  <input className="input-federal" required placeholder="Full name" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} data-testid="st-name" />
                  <input className="input-federal" required type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="st-email" />
                  <input className="input-federal" required placeholder="Phone (mobile preferred)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="st-phone" />
                  <input className="input-federal" required placeholder="Country" value={form.country} onChange={e => setForm({...form, country: e.target.value})} data-testid="st-country" />
                  <input className="input-federal" placeholder="City" value={form.city} onChange={e => setForm({...form, city: e.target.value})} data-testid="st-city" />
                  <input className="input-federal" placeholder="LinkedIn URL (optional)" value={form.linkedin} onChange={e => setForm({...form, linkedin: e.target.value})} data-testid="st-linkedin" />
                  <input
                    className="input-federal md:col-span-2"
                    required
                    type="number"
                    min={1}
                    placeholder="Annual sales target USD ($250,000+ qualifies)"
                    value={form.annual_sales_target}
                    onChange={e => setForm({...form, annual_sales_target: parseInt(e.target.value) || 0})}
                    data-testid="st-target"
                  />
                  <input className="input-federal md:col-span-2" required placeholder="Industries you've sold in" value={form.industries_sold} onChange={e => setForm({...form, industries_sold: e.target.value})} data-testid="st-industries" />
                  <label className="flex items-center gap-3 text-sm text-gray-800">
                    <input type="checkbox" checked={form.delaware_c_corp_familiar} onChange={e => setForm({...form, delaware_c_corp_familiar: e.target.checked})} className="w-4 h-4" data-testid="st-delaware" />
                    Familiar with Delaware C-Corp structure
                  </label>
                  <label className="flex items-center gap-3 text-sm text-gray-800">
                    <input type="checkbox" checked={form.work_mobile_only} onChange={e => setForm({...form, work_mobile_only: e.target.checked})} className="w-4 h-4" data-testid="st-mobile" />
                    Will work from smartphone
                  </label>
                  <textarea className="input-federal md:col-span-2" rows={5} required placeholder="Your pitch — why you'll close $250K+ in your first year" value={form.pitch} onChange={e => setForm({...form, pitch: e.target.value})} data-testid="st-pitch" />
                  <button type="submit" className="btn-signal md:col-span-2 justify-center" data-testid="st-submit">
                    Submit Power Sales Application <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
